<template>
  <manual-page
    title="Gestión de Franquicias"
    description="Guía completa para administrar franquicias y sucursales en la red Trunimal."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'franchisesIndex' }"
    action-text="Ver franquicias"
  >
    <!-- Overview -->
    <h2>Vista General del Módulo</h2>
    <p>
      El módulo de franquicias te permite administrar toda la red de operación de Trunimal.
      Cada franquicia representa un socio operativo que puede tener una o más sucursales
      físicas donde se prepara y distribuye el alimento.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Desde este módulo puedes:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Consultar y administrar todas las franquicias de la red</li>
        <li>Crear nuevas franquicias y asignar propietarios</li>
        <li>Gestionar las sucursales de cada franquicia</li>
        <li>Configurar rangos de envío y cobertura</li>
        <li>Ver estadísticas de clientes y órdenes por sucursal</li>
        <li>Administrar usuarios asignados a cada franquicia</li>
      </ul>
    </div>

    <!-- Franchise List -->
    <h2>Lista de Franquicias</h2>
    <p>
      La lista principal de franquicias muestra todas las franquicias registradas en el sistema.
      Puedes filtrar y buscar por nombre, propietario o estado.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información mostrada</h3>
          <p class="text-gray-600 mb-2">Cada franquicia en la lista muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre:</strong> Identificador de la franquicia</li>
            <li><strong>Propietario:</strong> Persona responsable</li>
            <li><strong>Sucursales:</strong> Cantidad de puntos de operación</li>
            <li><strong>Clientes activos:</strong> Número de clientes asignados</li>
            <li><strong>Estado:</strong> Activa o inactiva</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Haz clic en cualquier franquicia para acceder a su detalle
        y ver todas sus sucursales, usuarios y estadísticas.
      </p>
    </div>

    <!-- Creating a Franchise -->
    <h2>Crear una Nueva Franquicia</h2>
    <p>
      Para agregar una nueva franquicia a la red, sigue estos pasos:
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al formulario</h3>
          <p class="text-gray-600">
            Desde la lista de franquicias, haz clic en el boton "Crear franquicia"
            en la esquina superior derecha.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Completar datos de la franquicia</h3>
          <p class="text-gray-600 mb-2">Ingresa la siguiente informacion:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre:</strong> Nombre identificador de la franquicia</li>
            <li><strong>Propietario:</strong> Nombre del dueño o responsable</li>
            <li><strong>Email:</strong> Correo de contacto principal</li>
            <li><strong>Telefono:</strong> Numero de contacto</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Guardar y agregar sucursales</h3>
          <p class="text-gray-600">
            Una vez guardada la franquicia, podras agregar sucursales desde su vista de detalle.
            Cada franquicia debe tener al menos una sucursal para poder operar.
          </p>
        </div>
      </div>
    </div>

    <!-- Franchise Detail -->
    <h2>Detalle de Franquicia</h2>
    <p>
      La vista de detalle de una franquicia muestra toda la informacion relevante
      y permite gestionar sus sucursales y configuraciones.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">El detalle incluye:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Informacion general:</strong> Datos de contacto y propietario</li>
        <li><strong>Lista de sucursales:</strong> Todos los puntos de operacion</li>
        <li><strong>Usuarios asignados:</strong> Personal con acceso al portal de franquicia</li>
        <li><strong>Estadisticas:</strong> Clientes activos, planes, ordenes</li>
        <li><strong>Configuraciones:</strong> Parametros especiales de operacion</li>
      </ul>
    </div>

    <!-- Managing Branches -->
    <h2>Gestion de Sucursales</h2>
    <p>
      Las sucursales son los puntos fisicos donde se prepara el alimento y desde donde
      se realizan las entregas. Cada sucursal tiene su propia cobertura geografica.
    </p>

    <h3>Ver Sucursales</h3>
    <p>
      Desde el detalle de la franquicia, puedes ver todas las sucursales asociadas.
      Cada sucursal muestra:
    </p>
    <ul>
      <li><strong>Nombre:</strong> Identificador de la sucursal</li>
      <li><strong>Direccion:</strong> Ubicacion fisica completa</li>
      <li><strong>Telefono:</strong> Contacto de la sucursal</li>
      <li><strong>Rangos de envio:</strong> Zonas de cobertura configuradas</li>
      <li><strong>Clientes asignados:</strong> Numero de clientes que atiende</li>
      <li><strong>Ordenes activas:</strong> Pedidos en proceso</li>
    </ul>

    <h3>Crear una Sucursal</h3>
    <p>Para agregar una nueva sucursal a una franquicia:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder desde la franquicia</h3>
          <p class="text-gray-600">
            Entra al detalle de la franquicia y haz clic en "Agregar sucursal".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Completar datos de la sucursal</h3>
          <p class="text-gray-600 mb-2">Ingresa la informacion requerida:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre:</strong> Identificador de la sucursal</li>
            <li><strong>Direccion completa:</strong> Calle, numero, colonia, ciudad</li>
            <li><strong>Telefono:</strong> Numero de contacto</li>
            <li><strong>Horarios:</strong> Dias y horas de operacion</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Configurar rangos de envio</h3>
          <p class="text-gray-600">
            Define las zonas de cobertura y costos de envio. Esto determina
            que clientes pueden asignarse a esta sucursal.
          </p>
        </div>
      </div>
    </div>

    <!-- Branch Configuration -->
    <h2>Configuracion de Sucursal</h2>
    <p>
      Cada sucursal puede configurarse de forma independiente para optimizar su operacion.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Opciones de configuracion:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Rangos de envio:</strong> Define zonas por codigo postal o colonia, con costo y tiempo de entrega</li>
        <li><strong>Horarios de operacion:</strong> Dias y horas en que la sucursal prepara y entrega</li>
        <li><strong>Capacidad de produccion:</strong> Limites de ordenes por dia para evitar sobrecarga</li>
        <li><strong>Datos de contacto:</strong> Informacion visible para clientes</li>
      </ul>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Los rangos de envio son fundamentales. Un cliente solo puede
        asignarse a una sucursal si su direccion esta dentro de la zona de cobertura configurada.
      </p>
    </div>

    <!-- Shipping Ranges -->
    <h2>Rangos de Envio</h2>
    <p>
      Los rangos de envio determinan que areas geograficas puede atender cada sucursal
      y cual es el costo del envio para cada zona.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Definir zonas</h3>
          <p class="text-gray-600">
            Puedes crear zonas por codigo postal, colonia, municipio o ciudad.
            Cada zona puede tener un costo de envio diferente.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Establecer costos</h3>
          <p class="text-gray-600">
            Asigna el costo de envio para cada zona. Puedes tener zonas con envio
            gratuito y otras con costo adicional.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Tiempos de entrega</h3>
          <p class="text-gray-600">
            Define el tiempo estimado de entrega para cada zona. Esto ayuda
            a los clientes a saber cuando recibiran su pedido.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Revisa periodicamente los rangos de envio para expandir
        la cobertura o ajustar costos segun la demanda de cada zona.
      </p>
    </div>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualFranchises",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualUsers", label: "Usuarios y permisos" },
        { route: "manualOrders", label: "Ordenes" },
        { route: "manualAccounting", label: "Contabilidad" },
        { route: "manualReports", label: "Reportes" },
      ],
    };
  },
};
</script>
